import type { Point } from '../core/types';
import { applyGradient, renderGradientPreview, type GradientFill } from '../engine/ops';
import { commitHistory } from '../state/actions';
import { setLivePreview } from '../state/renderState';
import { paintableLayer, type Tool, type ToolContext } from './types';

/** Shift locks the axis to the nearest eighth of a turn, as in Photoshop. */
function constrain(start: Point, end: Point): Point {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const step = Math.PI / 4;
  const angle = Math.round(Math.atan2(dy, dx) / step) * step;
  const length = Math.hypot(dx, dy);
  return { x: start.x + Math.cos(angle) * length, y: start.y + Math.sin(angle) * length };
}

function fillFrom(ctx: ToolContext, start: Point, end: Point): GradientFill {
  const settings = ctx.gradient;
  return {
    gradient: settings.value,
    type: settings.type,
    start,
    end,
    opacity: settings.opacity,
    blendMode: settings.blendMode,
    reverse: settings.reverse,
    dither: settings.dither,
  };
}

export const gradientTool: Tool = (() => {
  let layerId: string | null = null;
  let start: Point | null = null;
  let end: Point | null = null;

  const release = (): void => {
    if (layerId) setLivePreview('surface', layerId, null);
    layerId = null;
    start = null;
    end = null;
  };

  return {
    id: 'gradient',
    cursor: 'crosshair',
    hint: 'Drag to set the axis; Shift keeps it straight',

    onPointerDown(event, ctx) {
      const target = paintableLayer(ctx.doc);
      if (!target) {
        ctx.setStatus('The active layer is locked, or has no pixels to fill');
        return;
      }
      layerId = target;
      start = event.point;
      end = event.point;
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (!start || !layerId) return;
      end = event.shift ? constrain(start, event.point) : event.point;
      const preview = renderGradientPreview(
        ctx.engine,
        ctx.doc,
        layerId,
        fillFrom(ctx, start, end),
      );
      setLivePreview('surface', layerId, preview);
      ctx.requestRender();
    },

    onPointerUp(event, ctx) {
      if (!start || !layerId) return;
      const finish = event.shift ? constrain(start, event.point) : event.point;
      const target = layerId;
      const from = start;
      release();

      // A click without a drag has no axis, so there is nothing to fill.
      if (Math.hypot(finish.x - from.x, finish.y - from.y) < 1) {
        ctx.requestRender();
        return;
      }
      const ops = applyGradient(ctx.engine, ctx.doc, target, fillFrom(ctx, from, finish));
      commitHistory(ctx.doc.id, 'Gradient', ops);
      ctx.requestRender();
    },

    cancel(ctx) {
      release();
      ctx.requestRender();
    },

    drawOverlay(overlay) {
      if (!start || !end) return;
      const a = overlay.toScreen(start);
      const b = overlay.toScreen(end);
      const { ctx: c } = overlay;
      c.strokeStyle = 'rgba(0,0,0,0.7)';
      c.lineWidth = 3;
      c.beginPath();
      c.moveTo(a.x, a.y);
      c.lineTo(b.x, b.y);
      c.stroke();
      c.strokeStyle = 'rgba(255,255,255,0.95)';
      c.lineWidth = 1;
      c.stroke();
    },
  };
})();
